import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Grid } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
  LogContainerFillEventInput,
  UnitType,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { getDateLikeIsoDate, toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  LOG_CONTAINER_FILL_EVENT_MUTATION,
  UPDATE_CONTAINER_FILL_EVENT_MUTATION,
  VALIDATE_CONTAINER_FILL_EVENT_INPUT,
} from "./api";
import { OCCURRED_AT_SHORTCUTS, UNIT_SELECT_ITEMS } from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type ContainerFillEvent = WasteContainerDetail["fillEvents"][number];

type FillEventFormValues = {
  direction: "ADD" | "REMOVE";
  amount: string;
  unit: UnitType | null;
  occurredAt: string | null;
  isEstimate: boolean;
  // Null once the field is cleared (the text field coerces "" to null).
  note: string | null;
};

const DIRECTION_OPTIONS = [
  { value: "ADD", label: "Add waste" },
  { value: "REMOVE", label: "Remove waste" },
];

function localIssue(key: string, message: string): Issue {
  return {
    level: IssueLevel.Critical,
    key,
    message,
    modelName: "WasteContainer",
    dataName: null,
    jurisdictions: [],
  };
}

/**
 * Maps the form onto the signed mutation input: the direction radio carries
 * the sign, the amount field carries the magnitude. Null when the form is
 * too incomplete to express an input at all.
 */
function toFillEventInput(
  values: FillEventFormValues,
  containerId: string
): LogContainerFillEventInput | null {
  const magnitude = safeParseFloat(values.amount);
  if (magnitude === null || !values.unit || !values.occurredAt) {
    return null;
  }
  const occurredAt = toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.occurredAt));
  if (!occurredAt) {
    return null;
  }
  return {
    containerId,
    amount: values.direction === "REMOVE" ? -Math.abs(magnitude) : Math.abs(magnitude),
    unit: values.unit,
    isEstimate: values.isEstimate,
    occurredAt,
    // The note field yields null once cleared (not ""), so guard the trim.
    note: values.note?.trim() || undefined,
  };
}

function incompleteFormIssues(values: FillEventFormValues): Issue[] {
  const issues: Issue[] = [];
  if (safeParseFloat(values.amount) === null) {
    issues.push(localIssue("amount", "Enter the amount"));
  }
  if (!values.unit) {
    issues.push(localIssue("unit", "Select a unit"));
  }
  if (!values.occurredAt) {
    issues.push(localIssue("occurredAt", "Enter the date this happened"));
  }
  return issues;
}

export type ContainerFillEventDialogProps = {
  container: WasteContainerDetail;
  /** Present when editing an existing event. */
  fillEvent?: ContainerFillEvent;
  onClose: () => void;
};

export function ContainerFillEventDialog({
  container,
  fillEvent,
  onClose,
}: ContainerFillEventDialogProps) {
  const alerts = useAlerts();
  const mode: "log" | "edit" = fillEvent ? "edit" : "log";

  const defaultValues = useMemo<FillEventFormValues>(
    () => ({
      direction: fillEvent && fillEvent.amount < 0 ? "REMOVE" : "ADD",
      amount: fillEvent ? String(Math.abs(fillEvent.amount)) : "",
      unit: fillEvent?.unit ?? container.amountUnit ?? container.capacityUnit ?? null,
      // Seed the date-only prefix: parsing the stored UTC-midnight instant in
      // the browser's local zone would show (and then save) the previous
      // calendar day for negative-UTC-offset users. New events default to
      // today — the capture-first common case.
      occurredAt: fillEvent?.occurredAt
        ? (getDateLikeIsoDate(fillEvent.occurredAt) ?? null)
        : DateTime.local().toISODate(),
      isEstimate: fillEvent?.isEstimate ?? container.isEstimate,
      note: fillEvent?.note ?? "",
    }),
    [fillEvent, container]
  );

  const [validateQuery] = useLazyQuery(VALIDATE_CONTAINER_FILL_EVENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  const validateInput = useCallback(
    async (values: FillEventFormValues): Promise<Issue[]> => {
      const input = toFillEventInput(values, container.id);
      if (!input) {
        return incompleteFormIssues(values);
      }
      const { data } = await validateQuery({
        variables: { input, currentFillEventId: fillEvent?.id },
      });
      return data?.containerFillEventInputValidator ?? [];
    },
    [validateQuery, container.id, fillEvent?.id]
  );

  const form = useValidatingForm<FillEventFormValues>(
    defaultValues,
    // Seed with the container's standing issues (over capacity, below-capacity
    // FULL) from the query that already loaded it, so an edit opens showing
    // them rather than only after the first change. A blank log form isn't
    // seeded — its own nags would just fire for fields the user hasn't
    // reached — so issues are seeded only when editing an existing event.
    mode === "edit" ? container.issues : [],
    validateInput,
    undefined,
    {
      // A background refetch mid-edit re-issues the defaults; keep what the
      // user has typed rather than resetting under their cursor.
      keepDirtyValuesOnReset: true,
    }
  );
  const {
    control,
    getValues,
    issues,
    formState: { isDirty, dirtyFields },
  } = form;

  const [logFillEvent, { loading: isLogging }] = useMutation(LOG_CONTAINER_FILL_EVENT_MUTATION, {
    refetchQueries: ["GetWasteContainer", "TenantWasteContainers", "FacilityWasteContainers"],
  });
  const [updateFillEvent, { loading: isUpdating }] = useMutation(
    UPDATE_CONTAINER_FILL_EVENT_MUTATION,
    {
      refetchQueries: ["GetWasteContainer", "TenantWasteContainers", "FacilityWasteContainers"],
    }
  );

  const onSubmit = async () => {
    const values = getValues();
    const input = toFillEventInput(values, container.id);
    if (!input) {
      alerts.error("Enter an amount, unit, and date");
      return;
    }
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "log") {
      await logFillEvent({
        variables: { input },
        onCompleted: () => {
          alerts.success("Fill event logged");
          onClose();
        },
        onError: (error) => {
          alerts.error(error.message ?? "An error occurred while logging the fill event");
        },
      });
      return;
    }
    if (!fillEvent) {
      return;
    }
    await updateFillEvent({
      variables: {
        input: {
          id: fillEvent.id,
          amount: input.amount,
          unit: input.unit,
          isEstimate: values.isEstimate,
          // Only send the date when the user actually touched it, so an
          // untouched value can never round-trip through the local zone.
          occurredAt: dirtyFields.occurredAt ? input.occurredAt : undefined,
          note: values.note?.trim() || null,
        },
      },
      onCompleted: () => {
        alerts.success("Fill event updated");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while updating the fill event");
      },
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={mode === "log" ? "Log fill event" : "Edit fill event"}
      primaryActionButtonProps={{
        loading: isLogging || isUpdating,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || !isDirty,
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <FormProvider {...form}>
        <Box component="form" sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <RadioGroupField
                name="direction"
                label="Direction"
                control={control}
                radioOptions={DIRECTION_OPTIONS}
              />
            </Grid>
            <Grid size={{ xs: 7 }}>
              <FormTextField
                name="amount"
                label="Amount"
                control={control}
                type="number"
                textFieldProps={{ required: true }}
              />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <FormSelect
                name="unit"
                label="Unit"
                control={control}
                selectItems={UNIT_SELECT_ITEMS}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <DateField
                name="occurredAt"
                label="Date"
                control={control}
                required
                shortcuts={OCCURRED_AT_SHORTCUTS}
                datePickerProps={{ disableFuture: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormTextField
                name="note"
                label="Note"
                control={control}
                textFieldProps={{ multiline: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <CheckboxField
                name="isEstimate"
                label="This amount is an estimate"
                control={control}
                noErrorDisplay
              />
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </Dialog>
  );
}
